import React from 'react';

import PageLayout from 'components/common/PageLayout';
import { SUBSCRIPTION_PLANS_PAGE } from 'components/constants';

import styles from './styles.module.scss';

export type AudiencePageData = {
  eyebrow: string;
  title: string;
  subtitle: string;
  heroPanelTitle: string;
  heroPanelItems: string[];
  audienceLabel: string;
  audienceItems: string[];
  industrySolutions?: {
    title: string;
    intro: string;
    items: Array<{
      industry: string;
      title: string;
      challenge: string;
      products: string[];
      data: string;
      value: string;
    }>;
  };
  useCasesTitle: string;
  useCasesIntro: string;
  useCases: Array<{ title: string; text: string; examples: string[] }>;
  workflowTitle: string;
  workflowIntro: string;
  workflowSteps: Array<{ title: string; text: string }>;
  foundationTitle: string;
  foundationIntro: string;
  foundation: Array<{ title: string; text: string }>;
  examplesTitle: string;
  examplesIntro: string;
  examples: Array<{ label: string; title: string; text: string }>;
  ctaTitle: string;
  ctaText: string;
};

type Props = { data: AudiencePageData };

const AudienceSolutionPage: React.FC<Props> = ({ data }) => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>{data.eyebrow}</p>
            <h1>{data.title}</h1>
            <p className={styles.heroSubtitle}>{data.subtitle}</p>
            <div className={styles.heroActions}>
              <a href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noopener noreferrer" className={styles.primaryCta}>
                Start creating
                <span aria-hidden="true">→</span>
              </a>
              <a href="#use-cases" className={styles.secondaryCta}>
                Explore use cases
              </a>
            </div>
          </div>

          <div className={styles.heroPanel} aria-hidden="true">
            <div className={styles.panelChrome}>
              <span />
              <span />
              <span />
              <p>Storycards workspace</p>
            </div>
            <div className={styles.panelBody}>
              <span className={styles.panelLabel}>Live product system</span>
              <h2>{data.heroPanelTitle}</h2>
              <div className={styles.panelItems}>
                {data.heroPanelItems.map((item, index) => (
                  <div key={item}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.panelStatus}>
              <i />
              Ready to publish and measure.
            </div>
          </div>
        </div>
      </section>

      <section className={styles.audienceBand} aria-label={data.audienceLabel}>
        <div className={styles.sectionInner}>
          <p>{data.audienceLabel}</p>
          <div>
            {data.audienceItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
      </section>

      {data.industrySolutions && (
        <section className={styles.industrySolutions} id="use-cases">
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <h2>{data.industrySolutions.title}</h2>
              <p>{data.industrySolutions.intro}</p>
            </div>
            <div className={styles.industryList}>
              {data.industrySolutions.items.map((item, index) => (
                <article className={styles.industryItem} key={item.industry}>
                  <div className={styles.industryName}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <p>{item.industry}</p>
                  </div>
                  <div className={styles.industryChallenge}>
                    <p className={styles.detailLabel}>The opportunity</p>
                    <h3>{item.title}</h3>
                    <p>{item.challenge}</p>
                  </div>
                  <div className={styles.industryOutcome}>
                    <p className={styles.detailLabel}>Products to build</p>
                    <ul>
                      {item.products.map((product) => (
                        <li key={product}>{product}</li>
                      ))}
                    </ul>
                    <div className={styles.dataValue}>
                      <div>
                        <span>Data created</span>
                        <p>{item.data}</p>
                      </div>
                      <div>
                        <span>Business value</span>
                        <p>{item.value}</p>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={styles.useCases} id={data.industrySolutions ? undefined : 'use-cases'}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2>{data.useCasesTitle}</h2>
            <p>{data.useCasesIntro}</p>
          </div>
          <div className={styles.useCaseGrid}>
            {data.useCases.map((item, index) => (
              <article className={styles.useCase} key={item.title}>
                <span className={styles.index}>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
                <ul>
                  {item.examples.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.workflow}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeaderDark}>
            <h2>{data.workflowTitle}</h2>
            <p>{data.workflowIntro}</p>
          </div>
          <div className={styles.workflowGrid}>
            {data.workflowSteps.map((step, index) => (
              <article key={step.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.foundation}>
        <div className={styles.sectionInner}>
          <div className={styles.foundationGrid}>
            <div className={styles.foundationIntro}>
              <h2>{data.foundationTitle}</h2>
              <p>{data.foundationIntro}</p>
            </div>
            <div className={styles.foundationItems}>
              {data.foundation.map((item) => (
                <article key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {data.examples.length > 0 && (
        <section className={styles.examples}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <h2>{data.examplesTitle}</h2>
              <p>{data.examplesIntro}</p>
            </div>
            <div className={styles.exampleGrid}>
              {data.examples.map((example) => (
                <article key={example.title}>
                  <span>{example.label}</span>
                  <h3>{example.title}</h3>
                  <p>{example.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <h2>{data.ctaTitle}</h2>
          <div>
            <p>{data.ctaText}</p>
            <a href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noopener noreferrer">
              Start creating
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default AudienceSolutionPage;
